/**
 * <s-product-row> — Swipeable product row with previous/next arrows.
 *
 * The track is a native horizontal scroll-snap row, so touch and trackpads
 * glide natively. The arrows scroll by one visible "page" of cards and are
 * hidden at each end. Respects reduced motion.
 *
 * Required child markup:
 *   [data-row-track] — Scroll-snap row of cards.
 *   [data-row-prev]  — Previous button.
 *   [data-row-next]  — Next button.
 */
class SProductRow extends HTMLElement {
  #ac;
  #track;
  #prev;
  #next;
  #frame = 0;
  #observer;

  connectedCallback() {
    this.#track = this.querySelector('[data-row-track]');
    if (!this.#track) return;
    this.#prev = this.querySelector('[data-row-prev]');
    this.#next = this.querySelector('[data-row-next]');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#prev?.addEventListener('click', () => this.#page(-1), opts);
    this.#next?.addEventListener('click', () => this.#page(1), opts);
    this.#track.addEventListener('scroll', () => this.#schedule(), { ...opts, passive: true });
    window.addEventListener('resize', () => this.#schedule(), opts);
    this.#observer = new ResizeObserver(() => this.#schedule());
    this.#observer.observe(this.#track);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #page(direction) {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const amount = this.#track.clientWidth * 0.9 * direction * (rtl ? -1 : 1);
    this.#track.scrollBy({ left: amount, behavior: reduce ? 'auto' : 'smooth' });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    this.toggleAttribute('data-at-start', position <= 2);
    this.toggleAttribute('data-at-end', position >= max - 2);
    if (this.#prev) this.#prev.disabled = position <= 2;
    if (this.#next) this.#next.disabled = position >= max - 2;
  }
}

if (!customElements.get('s-product-row')) customElements.define('s-product-row', SProductRow);
